// src/components/ErrorBoundary.tsx
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Center, // Used for centering the fallback UI
} from "@chakra-ui/react";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null; // Store errorInfo as well if needed for display or logging
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(
    error: Error
  ): Pick<State, "hasError" | "error"> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
    // Example: logErrorToMyService(error, errorInfo);
  }

  private handleRetry = () => {
    // Reset the error state to attempt to re-render the children
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Note: This is a simple retry by re-rendering.
    // If the error is from an async operation like a fetch,
    // this won't automatically retry that operation unless the child components
    // are designed to do so on re-mount or via props.
    // For React Query, query retries are handled internally by the library.
    // This boundary is more for rendering errors or unhandled exceptions.
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Center
          p={4}
          minHeight={{ base: "auto", md: "calc(100vh - 200px)" }}
          my={{ base: 4, md: 8 }}
        >
          {" "}
          {/* Adjust minHeight as needed */}
          <Alert
            status="error"
            variant="subtle" // Or "solid" for more emphasis
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="lg" // More rounded corners
            p={{ base: 4, md: 8 }} // Responsive padding
            maxWidth="lg" // Limit width for better readability
            boxShadow="lg" // Add some shadow
            bg="red.50" // Softer red background
            borderColor="red.200"
            borderWidth="1px"
          >
            <AlertIcon boxSize={{ base: "32px", md: "40px" }} color="red.500" />
            <AlertTitle
              mt={4}
              mb={2}
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="bold"
              color="red.700"
            >
              Oops! Something went wrong.
            </AlertTitle>
            <AlertDescription maxWidth="md" color="red.600">
              {this.props.fallbackMessage ||
                this.state.error?.message ||
                "An unexpected error occurred. We are sorry for the inconvenience."}
            </AlertDescription>
            {/* Optional: Display more error details in dev mode
            {import.meta.env.DEV && this.state.error && (
              <Box mt={4} p={2} bg="red.100" borderRadius="md" overflow="auto" maxH="150px" textAlign="left">
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8em' }}>
                  {this.state.error.stack || this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </Box>
            )}
            */}
            <Button
              mt={6}
              colorScheme="red"
              variant="outline"
              onClick={this.handleRetry}
            >
              Try Again
            </Button>
          </Alert>
        </Center>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
